import Button from '@jetbrains/ring-ui/components/button/button'
import {Caption} from '@jetbrains/ring-ui/components/button-group/button-group'
import Dropdown from '@jetbrains/ring-ui/components/dropdown/dropdown'
import PopupMenu from '@jetbrains/ring-ui/components/popup-menu/popup-menu'
import classNames from 'classnames'
import * as React from 'react'

import {FilterBuildStatus} from '../../Rules.types'
import {LoadingContext, DispatchContext} from '../CleanupForm.contexts'
import styles from '../CleanupForm.css'

type StatusItem = {
  status: FilterBuildStatus
  label: string
}

const defaultItem = {
  status: FilterBuildStatus.ANY,
  label: 'Any status',
} as const

const statusItems: ReadonlyArray<StatusItem> = [
  defaultItem,
  {
    status: FilterBuildStatus.SUCCESSFUL,
    label: 'Only successful',
  },
  {
    status: FilterBuildStatus.FAILED,
    label: 'Only failed',
  },
]

type OwnProps = {
  value: StatusItem
}
export const buildStatusConverter = {
  toRule: (
    value: StatusItem | null | undefined,
  ):
    | {
        status: FilterBuildStatus
      }
    | null
    | undefined =>
    !value || value.status === 'any'
      ? null
      : {
          status: value.status,
        },
  toForm: (
    value:
      | {
          status: FilterBuildStatus
        }
      | null
      | undefined,
  ): StatusItem => {
    const key = value?.status ?? 'any'
    return statusItems.find(item => item.status === key) ?? defaultItem
  },
}

function BuildStatusSection({value}: OwnProps) {
  const disabled = React.useContext(LoadingContext)
  const setValue = React.useContext(DispatchContext)
  const onChange = React.useCallback(
    (values: StatusItem) =>
      setValue?.({
        name: 'status',
        value: values,
      }),
    [setValue],
  )
  const handleSelect = React.useCallback(
    (option: StatusItem) => {
      onChange({...(statusItems.find(item => item.status === option.status) ?? defaultItem)})
    },
    [onChange],
  )
  const selectedItem = React.useMemo(
    () => statusItems.find(item => item.status === value.status) ?? defaultItem,
    [value.status],
  )
  return (
    <div className={styles.row}>
      <Caption className={styles.caption}>{'Status'}</Caption>
      <div className={styles.content}>
        <div className={classNames(styles.inputs)}>
          <Dropdown
            anchor={
              <Button disabled={disabled} dropdown text className={styles.textButton}>
                {selectedItem.label}
              </Button>
            }
          >
            <PopupMenu onSelect={handleSelect} closeOnSelect data={statusItems} />
          </Dropdown>
        </div>
        <div className={styles.hint}>
          {
            'Select "Any status" to preserve even canceled\u00a0and\u00a0failed\u00a0to\u00a0start builds'
          }
        </div>
      </div>
    </div>
  )
}

export default React.memo<OwnProps>(BuildStatusSection)
